import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../App';
import { projectsAPI, jobCardsAPI, dashboardAPI, operatorActivitiesAPI, usersAPI, editLogsAPI, estimatesAPI, reportsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import appLogo from '../assets/logo.png';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import PerformanceReports from './PerformanceReports';
import NotificationBell from './NotificationBell';
import DateFilter from './DateFilter';
import OperatorEditHistory from './OperatorEditHistory';
import PressingBalance from './PressingBalance';
import WorkLogHistory from './WorkLogHistory';
import { BillView } from './BillView';
import FloorStatus from './FloorStatus';
import { pressingProgress } from '../utils/pressing';
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  LogOut,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Scissors,
  Layers,
  CircleDot,
  Activity,
  User,
  Filter,
  BarChart3,
  History,
  Search,
  Edit,
  Eye,
  Receipt,
  Image,
  Download,
  FileSpreadsheet,
  Trash2,
  IndianRupee,
  RotateCw,
  Users
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const OwnerDashboard = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [projects, setProjects] = useState([]);
  const [jobCards, setJobCards] = useState([]);
  const [stats, setStats] = useState(null);
  const [todayActivities, setTodayActivities] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [selectedCoordinator, setSelectedCoordinator] = useState('all');
  const [projectSearch, setProjectSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editLogs, setEditLogs] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [auditSearch, setAuditSearch] = useState('');
  const [showEstimateDetail, setShowEstimateDetail] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [selectedEstimateLogs, setSelectedEstimateLogs] = useState([]);
  const [viewJobCard, setViewJobCard] = useState(null);
  const [viewingPressingImage, setViewingPressingImage] = useState(null);
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const [statsDialogType, setStatsDialogType] = useState(null); // bucket key
  const [bucketCards, setBucketCards] = useState(null); // list behind the tapped card
  const [receivables, setReceivables] = useState(null);
  const [showReceivables, setShowReceivables] = useState(false);
  const [failedSections, setFailedSections] = useState([]);

  // Production trend strip (overview tab)
  const [trendGranularity, setTrendGranularity] = useState('day');
  const [trendData, setTrendData] = useState(null);

  // Clients tab
  const [clientsReport, setClientsReport] = useState(null);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [newOnly, setNewOnly] = useState(false);

  // Date filter state for job cards
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFilterStart, setDateFilterStart] = useState(null);
  const [dateFilterEnd, setDateFilterEnd] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // day view shows the last 7 days; week/month use longer windows
    const period = trendGranularity === 'day' ? 'weekly' : trendGranularity === 'week' ? 'monthly' : 'current';
    reportsAPI.getProductionTrends(trendGranularity, period)
      .then(setTrendData)
      .catch(() => setTrendData(null));
  }, [trendGranularity]);

  useEffect(() => {
    if (activeTab === 'clients' && !clientsReport && !clientsLoading) {
      setClientsLoading(true);
      reportsAPI.getClients()
        .then(setClientsReport)
        .catch(() => toast({ title: "Error", description: "Failed to load client KPIs", variant: "destructive" }))
        .finally(() => setClientsLoading(false));
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each request has its own fallback so one transient network failure can't
  // zero out the whole dashboard (all-or-nothing Promise.all caused blank
  // dashboards on flaky mobile connections). Failed sections are tracked and
  // retried once automatically before showing the retry banner.
  const FAIL = Symbol('fetch-failed');
  const fetchData = async (isRetry = false) => {
    // Only show the full-screen spinner on the very first load - a manual refresh
    // must not blank a dashboard that already has data on it.
    if (!isRetry && !stats) setLoading(true);
    const wrap = (p) => p.catch(() => FAIL);
    const [projectsData, jobCardsData, statsData, activitiesData, coordsData, editLogsData, estimatesData, receivablesData] = await Promise.all([
      wrap(projectsAPI.getAll()),
      wrap(jobCardsAPI.getAll()),
      wrap(dashboardAPI.getStats()),
      wrap(operatorActivitiesAPI.getAll({ today_only: true })),
      wrap(usersAPI.getCoordinators()),
      wrap(editLogsAPI.getAll()),
      wrap(estimatesAPI.getAll()),
      wrap(reportsAPI.getReceivables())
    ]);

    const failed = [];
    if (projectsData !== FAIL) setProjects(projectsData); else failed.push('Projects');
    if (jobCardsData !== FAIL) setJobCards(jobCardsData); else failed.push('Job Cards');
    if (statsData !== FAIL) setStats(statsData); else failed.push('Stats');
    if (activitiesData !== FAIL) setTodayActivities(activitiesData); else failed.push('Activities');
    if (coordsData !== FAIL) setCoordinators(coordsData); else failed.push('Coordinators');
    if (editLogsData !== FAIL) setEditLogs(editLogsData); else failed.push('Audit Logs');
    if (estimatesData !== FAIL) setEstimates(estimatesData); else failed.push('Estimates');
    if (receivablesData !== FAIL) setReceivables(receivablesData); else failed.push('Receivables');

    if (failed.length > 0 && !isRetry) {
      // One silent retry after 2s absorbs one-off network blips
      setTimeout(() => fetchData(true), 2000);
      return;
    }
    setFailedSections(failed);
    setLoading(false);
    // Clients report is lazily loaded; drop it so the next visit refetches
    // instead of showing a session-old snapshot.
    if (isRetry || failed.length === 0) setClientsReport(null);
  };

  const filterByDate = (createdAt) => {
    if (!createdAt) return true;
    if (dateFilter === 'all') return true;
    
    const itemDate = new Date(createdAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    switch (dateFilter) {
      case 'today':
        return itemDate >= today;
      case 'yesterday':
        const todayStart = new Date(today);
        return itemDate >= yesterday && itemDate < todayStart;
      case 'weekly':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return itemDate >= weekAgo;
      case 'monthly':
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        return itemDate >= monthAgo;
      case 'custom':
        if (dateFilterStart && dateFilterEnd) {
          const start = new Date(dateFilterStart);
          start.setHours(0, 0, 0, 0);
          const end = new Date(dateFilterEnd);
          end.setHours(23, 59, 59, 999);
          return itemDate >= start && itemDate <= end;
        }
        return true;
      default:
        return true;
    }
  };

  const filteredProjects = useMemo(() => {
    let filtered = projects;
    if (selectedCoordinator !== 'all') {
      filtered = filtered.filter(p => p.created_by_id === selectedCoordinator);
    }
    if (projectSearch.trim()) {
      const q = projectSearch.trim().toLowerCase();
      filtered = filtered.filter(p =>
        (p.client_name || '').toLowerCase().includes(q) ||
        (p.job_card_number || '').toLowerCase().includes(q) ||
        (p.project_number || '').toLowerCase().includes(q) ||
        (p.reference || '').toLowerCase().includes(q) ||
        (p.created_by_name || '').toLowerCase().includes(q)
      );
    }
    // Apply date filter
    filtered = filtered.filter(p => filterByDate(p.created_at));
    return filtered;
  }, [projects, selectedCoordinator, projectSearch, dateFilter, dateFilterStart, dateFilterEnd]);

  const handleCopyNewClients = async () => {
    const list = (clientsReport?.clients || [])
      .filter(c => c.is_new)
      .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-';
    const text = [
      `New Clients - ${new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })} (${list.length})`,
      'Interior Job Work', '',
      ...list.map((c, i) => `${i + 1}. ${c.name}  |  ${fmt(c.created_at)}  |  Coord: ${(c.coordinators || []).join(', ') || '-'}`),
    ].join(String.fromCharCode(10));
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copied', description: `${list.length} new clients copied - paste into WhatsApp.` });
    } catch {
      toast({ title: 'Copy failed', description: 'Long-press to select the list instead.', variant: 'destructive' });
    }
  };

  const filteredJobCards = useMemo(() => {
    let filtered = jobCards;
    if (selectedCoordinator !== 'all') {
      filtered = filtered.filter(jc => jc.created_by_id === selectedCoordinator);
    }
    // Apply date filter
    filtered = filtered.filter(jc => filterByDate(jc.created_at));
    return filtered;
  }, [jobCards, selectedCoordinator, dateFilter, dateFilterStart, dateFilterEnd]);

  const filteredEditLogs = useMemo(() => {
    if (!auditSearch.trim()) return editLogs;
    const q = auditSearch.toLowerCase();
    return editLogs.filter(log => 
      (log.estimate_number || '').toLowerCase().includes(q) ||
      (log.job_card_number || '').toLowerCase().includes(q) ||
      (log.customer_name || '').toLowerCase().includes(q) ||
      (log.edited_by_name || '').toLowerCase().includes(q)
    );
  }, [editLogs, auditSearch]);

  const editedEstimates = useMemo(() => {
    return estimates.filter(e => e.edit_count > 0).sort((a, b) => 
      new Date(b.last_edited_at || 0) - new Date(a.last_edited_at || 0)
    );
  }, [estimates]);

  const handleViewEstimateDetail = async (est) => {
    setSelectedEstimate(est);
    try {
      const logs = await editLogsAPI.getByEstimate(est.id);
      setSelectedEstimateLogs(logs);
    } catch {
      setSelectedEstimateLogs([]);
    }
    setShowEstimateDetail(true);
  };

  const handleOpenJobCard = (jcNumber) => {
    const jc = jobCards.find(j => j.job_card_number === jcNumber);
    if (jc) {
      setViewJobCard(jc);
      setActiveTab('projects');
    } else {
      // Try from projects
      const proj = projects.find(p => (p.job_card_number || p.project_number) === jcNumber);
      if (proj) {
        const linkedJC = jobCards.find(j => j.project_id === proj.id);
        if (linkedJC) {
          setViewJobCard(linkedJC);
        }
      }
      setActiveTab('projects');
    }
  };

  const handleDeleteJobCard = async (jobCardId) => {
    try {
      await jobCardsAPI.deleteJobCard(jobCardId);
      toast({ title: "Deleted", description: "Job card and all associated data permanently deleted" });
      setViewJobCard(null);
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to delete job card", variant: "destructive" });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_production': return 'bg-emerald-100 text-emerald-800';
      case 'in_cutting': return 'bg-yellow-100 text-yellow-800';
      case 'in_pressing': return 'bg-orange-100 text-orange-800';
      case 'in_boring': return 'bg-purple-100 text-purple-800';
      case 'finishing': return 'bg-indigo-100 text-indigo-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'invoiced': return 'bg-emerald-100 text-emerald-800';
      case 'in_progress': return 'bg-amber-100 text-amber-800';
      case 'pending_supervisor_review': return 'bg-orange-100 text-orange-800';
      case 'sent_to_accounts': return 'bg-teal-100 text-teal-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isDelayed = (jc) => {
    if (!jc.pushed_to_production_at) return false;
    if (['closed', 'completed', 'sent_to_accounts', 'pending_supervisor_review', 'cancelled'].includes(jc.status)) return false;
    const pushedDate = new Date(jc.pushed_to_production_at);
    const threeWeeksMs = 21 * 24 * 60 * 60 * 1000;
    return (Date.now() - pushedDate.getTime()) > threeWeeksMs;
  };

  const getCompletionPct = (project) => {
    if (project.status === 'completed' || project.status === 'invoiced') return 100;
    const jc = jobCards.find(j => j.project_id === project.id);
    if (!jc) return 0;
    if (jc.status === 'completed' || jc.status === 'sent_to_accounts') return 100;
    if (jc.status === 'pending_supervisor_review') return 90;
    // Calculate from coordinator tasks + data entry
    let steps = 0;
    let done = 0;
    const tasks = jc.coordinator_tasks || [];
    steps += 3; // 3 coordinator tasks
    done += tasks.filter(t => t.status === 'completed').length;
    // Check if pressing/cutting/boring data entered
    if (jc.pressing_qty_total > 0) { done++; }
    steps++;
    if (jc.cutting_qty_total > 0) { done++; }
    steps++;
    if ((jc.finishing_lines || []).some(f => f.qty > 0)) { done++; }
    steps++;
    if (jc.status === 'in_production' || jc.status === 'in_progress') { done++; }
    steps++;
    return steps > 0 ? Math.round((done / steps) * 100) : 0;
  };

  // Get filtered job cards for stats dialog
  // The list behind a stat card comes from the same backend bucket that produced
  // the number, so the two can never drift apart.
  const getStatsJobCards = () => bucketCards || [];

  const BUCKET_META = {
    in_production:  { title: 'In Production',   color: 'text-blue-600',   desc: 'Job cards being worked on right now' },
    awaiting_start: { title: 'Awaiting Start',  color: 'text-amber-600',  desc: 'Created but not yet pushed to production' },
    finished:       { title: 'Finished',        color: 'text-green-600',  desc: 'Work complete and sent to accounts' },
    closed:         { title: 'Closed & Billed', color: 'text-slate-600',  desc: 'Fully paid and closed' },
    cancelled:      { title: 'Cancelled',       color: 'text-gray-500',   desc: 'Cancelled job cards (excluded from all totals)' },
    overdue:        { title: 'Overdue',         color: 'text-red-600',    desc: 'In production for more than 3 weeks' },
  };
  const getStatsDialogTitle = () => BUCKET_META[statsDialogType]?.title || 'Job Cards';
  const getStatsDialogColor = () => BUCKET_META[statsDialogType]?.color || 'text-gray-600';

  const handleStatCardClick = async (type) => {
    setStatsDialogType(type);
    setShowStatsDialog(true);
    setBucketCards(null);
    try {
      setBucketCards(await jobCardsAPI.getByBucket(type));
    } catch {
      setBucketCards([]);
    }
  };

  const handleStatsJobCardClick = (jc) => {
    setShowStatsDialog(false);
    setViewJobCard(jc);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
              <img src={appLogo} alt="Interior Job Work" className="w-[60px] h-[60px] object-cover" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Interior Job Work</h1>
              <p className="text-xs text-gray-500">Owner Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 hidden sm:block">Welcome, {user?.name}</span>
            <Button variant="ghost" size="sm" onClick={() => fetchData()} title="Refresh data" data-testid="refresh-dashboard">
              <RotateCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <NotificationBell />
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {failedSections.length > 0 && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2" data-testid="fetch-error-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 text-sm">
            <span className="text-red-700">
              Couldn't load: {failedSections.join(', ')}. Check your connection.
            </span>
            <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-100 h-7" onClick={() => fetchData()} data-testid="retry-fetch">
              <RotateCw className="w-3 h-3 mr-1" /> Retry
            </Button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-4 w-full lg:w-[560px] h-auto p-1.5 gap-1 bg-slate-100 rounded-xl">
            {/* Full Tailwind class strings, not interpolated - the build only
                keeps classes it can see literally in the source. */}
            {[
              { value: 'overview', label: 'Overview',  Icon: LayoutDashboard,
                cls: 'hover:bg-blue-50 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700', icon: 'text-blue-500' },
              { value: 'projects', label: 'Job Cards', Icon: FolderKanban,
                cls: 'hover:bg-violet-50 data-[state=active]:bg-violet-100 data-[state=active]:text-violet-700', icon: 'text-violet-500' },
              { value: 'clients',  label: 'Clients',   Icon: Users,
                cls: 'hover:bg-emerald-50 data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700', icon: 'text-emerald-500' },
              { value: 'reports',  label: 'Reports',   Icon: BarChart3,
                cls: 'hover:bg-amber-50 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-700', icon: 'text-amber-500' },
            ].map(({ value, label, Icon, cls, icon }) => (
              <TabsTrigger
                key={value}
                value={value}
                data-testid={`${value}-tab`}
                className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs sm:text-sm font-medium text-slate-500 transition-all data-[state=active]:shadow-sm ${cls}`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${icon}`} />
                <span className="truncate">{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {/* Main Stats - one card per job-card bucket. The counts and the
                lists behind them both come from the backend's JC_BUCKETS map,
                so a card can never disagree with what it opens. */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                { key: 'in_production',  label: 'In Production', value: stats?.jc_in_production,  accent: 'text-blue-600',   ring: 'hover:border-blue-400',   Icon: FolderKanban },
                { key: 'awaiting_start', label: 'Awaiting Start', value: stats?.jc_awaiting_start, accent: 'text-amber-600',  ring: 'hover:border-amber-400',  Icon: FileText },
                { key: 'finished',       label: 'Finished',       value: stats?.jc_finished,       accent: 'text-green-600',  ring: 'hover:border-green-400',  Icon: CheckCircle },
                { key: 'closed',         label: 'Closed & Billed', value: stats?.jc_closed,        accent: 'text-slate-600',  ring: 'hover:border-slate-400',  Icon: Receipt },
              ].map(({ key, label, value, accent, ring, Icon }) => (
                <Card
                  key={key}
                  className={`cursor-pointer border transition-all hover:shadow-md ${ring}`}
                  onClick={() => handleStatCardClick(key)}
                  data-testid={`stat-card-${key}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 truncate">{label}</p>
                        <p className={`text-3xl font-bold ${accent}`}>{value ?? 0}</p>
                      </div>
                      <Icon className={`w-8 h-8 flex-shrink-0 opacity-30 ${accent}`} />
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Overdue is the one card that needs action, so it keeps the colour */}
              <Card
                className="bg-gradient-to-br from-red-500 to-red-600 text-white cursor-pointer hover:shadow-lg transition-all"
                onClick={() => handleStatCardClick('overdue')}
                data-testid="stat-card-overdue"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs text-red-100 truncate">Overdue</p>
                      <p className="text-3xl font-bold">{stats?.jc_overdue ?? 0}</p>
                    </div>
                    <AlertCircle className="w-8 h-8 flex-shrink-0 text-red-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* What's pending at each stage */}
            <FloorStatus jobCards={jobCards} onOpenCard={(jc) => setViewJobCard(jc)} />

            {/* Money owed - the largest number in the business, and the one
                the dashboard never used to show. */}
            {receivables && (
              <Card
                className="border-emerald-200 bg-emerald-50/60 cursor-pointer hover:shadow-md transition-all"
                onClick={() => setShowReceivables(true)}
                data-testid="receivables-band"
              >
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="text-xs text-emerald-700/80">Outstanding from clients</p>
                      <p className="text-3xl font-bold text-emerald-700">
                        ₹{(receivables.outstanding || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </p>
                      <p className="text-xs text-emerald-700/70 mt-0.5">
                        across {receivables.bill_count} bill{receivables.bill_count === 1 ? '' : 's'}
                        {receivables.never_sent_count > 0 && ` · ${receivables.never_sent_count} never sent to client`}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {['0-30', '31-60', '61-90', '90+'].map(band => (
                        <div key={band} className="px-3 py-1.5 rounded-lg bg-white border border-emerald-200 text-center min-w-[76px]">
                          <p className="text-[10px] text-gray-500">{band} days</p>
                          <p className="text-sm font-semibold text-gray-800">
                            ₹{Math.round((receivables.ageing?.[band] || 0) / 1000).toLocaleString('en-IN')}k
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Today's Production */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Today's Production Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <Scissors className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                    <p className="text-3xl font-bold text-blue-600">{stats?.today_cutting_qty || 0}</p>
                    <p className="text-sm text-gray-600">Cutting</p>
                    <p className="text-[11px] text-gray-400">sheets</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <Layers className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                    <p className="text-3xl font-bold text-amber-600">{stats?.today_pressing_qty || 0}</p>
                    <p className="text-sm text-gray-600">Pressing</p>
                    <p className="text-[11px] text-gray-400">sheet-sides</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg text-center">
                    <CircleDot className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                    <p className="text-3xl font-bold text-purple-600">{stats?.today_boring_qty || 0}</p>
                    <p className="text-sm text-gray-600">Boring</p>
                    <p className="text-[11px] text-gray-400">holes</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Production Trends */}
            <Card data-testid="production-trends-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Production Trends
                  </CardTitle>
                  <div className="flex gap-1">
                    {[['day', 'Daily'], ['week', 'Weekly'], ['month', 'Monthly']].map(([key, label]) => (
                      <Button
                        key={key}
                        size="sm"
                        variant={trendGranularity === key ? 'default' : 'outline'}
                        className="h-7 text-xs"
                        onClick={() => setTrendGranularity(key)}
                        data-testid={`trend-${key}`}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const buckets = (trendData?.buckets || []).slice(trendGranularity === 'day' ? -7 : trendGranularity === 'week' ? -8 : -6);
                  if (buckets.length === 0) {
                    return <p className="text-sm text-gray-500 py-6 text-center">No production data for this period yet</p>;
                  }
                  const maxVal = Math.max(1, ...buckets.flatMap(b => [b.cutting_qty, b.pressing_qty, b.boring_qty]));
                  const fmtLabel = (b) => {
                    if (trendGranularity === 'day') {
                      const d = new Date(b + 'T00:00:00');
                      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
                    }
                    if (trendGranularity === 'week') return b.split('-')[1];
                    const [y, m] = b.split('-');
                    return new Date(`${y}-${m}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
                  };
                  const series = [
                    ['cutting_qty', 'bg-blue-500'],
                    ['pressing_qty', 'bg-amber-500'],
                    ['boring_qty', 'bg-purple-500'],
                  ];
                  return (
                    <div>
                      <div className="flex items-end gap-2 overflow-x-auto pb-1">
                        {buckets.map(b => (
                          <div key={b.bucket} className="flex-1 min-w-[56px]">
                            <div className="flex items-end justify-center gap-1 h-28">
                              {series.map(([key, color]) => (
                                <div key={key} className="flex flex-col items-center justify-end flex-1 h-full">
                                  <span className="text-[10px] text-gray-500 leading-none mb-0.5">{b[key] > 0 ? b[key] : ''}</span>
                                  <div
                                    className={`${color} w-full max-w-[18px] rounded-t`}
                                    style={{ height: `${Math.max(b[key] > 0 ? 4 : 0, (b[key] / maxVal) * 100)}%` }}
                                    title={`${key.replace('_qty', '')}: ${b[key]}`}
                                  />
                                </div>
                              ))}
                            </div>
                            <p className="text-[11px] text-gray-600 text-center mt-1 whitespace-nowrap">{fmtLabel(b.bucket)}</p>
                            <p className="text-[10px] text-gray-400 text-center" title="Job cards created / closed">
                              JC {b.job_cards_created}/{b.job_cards_closed}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-center gap-4 mt-2 text-xs text-gray-600">
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500 inline-block" /> Cutting</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500 inline-block" /> Pressing</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-purple-500 inline-block" /> Boring</span>
                        <span className="text-gray-400">JC = job cards created/closed</span>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Recent Job Cards */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Job Cards</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {projects.slice(0, 5).map(project => {
                    const jcNum = project.job_card_number || project.project_number;
                    const linkedJC = jobCards.find(j => j.project_id === project.id);
                    return (
                    <div 
                      key={project.id} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-blue-50 hover:shadow-sm transition-all"
                      onClick={() => handleOpenJobCard(jcNum)}
                      data-testid={`overview-jc-${jcNum}`}
                    >
                      <div>
                        <p className="font-medium text-blue-700 underline decoration-dotted">{jcNum}</p>
                        <p className="text-sm text-gray-500">{project.client_name}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          {project.created_by_name && (
                            <p className="text-xs text-blue-600 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {project.created_by_name}
                            </p>
                          )}
                          {(linkedJC?.created_at || project.created_at) && (
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(linkedJC?.created_at || project.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-sm font-medium text-blue-600">{getCompletionPct(project)}%</p>
                        </div>
                        <Badge className={getStatusColor(linkedJC?.status || project.status)}>
                          {(linkedJC?.status || project.status)?.replace(/_/g, ' ')}
                        </Badge>
                        {linkedJC && isDelayed(linkedJC) && (
                          <Badge className="bg-red-100 text-red-700 border border-red-300 animate-pulse">Delayed</Badge>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Today's Activity - folded in from the old Activity tab. It is a
                short daily list, not somewhere the owner needs to navigate to. */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="w-4 h-4 text-rose-500" />
                  Today's Activity
                  {todayActivities.length > 0 && (
                    <Badge variant="secondary">{todayActivities.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todayActivities.length > 0 ? (
                  <div className="divide-y">
                    {todayActivities.slice(0, 12).map(activity => (
                      <div key={activity.id} className="flex items-center justify-between py-2 gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{activity.operator_name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {activity.operation_type} · JC {activity.job_card_number}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-blue-600 text-sm">{activity.quantity}</p>
                          <p className="text-[11px] text-gray-400">
                            {activity.timestamp ? new Date(activity.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-6 text-sm">No activity logged today yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-4" data-testid="clients-content">
            {clientsLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : clientsReport ? (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500">Total Clients</p>
                      <p className="text-3xl font-bold text-blue-600">{clientsReport.total_clients}</p>
                    </CardContent>
                  </Card>
                  <Card
                    className={`cursor-pointer transition-shadow hover:shadow-md ${newOnly ? 'ring-2 ring-green-500' : ''}`}
                    onClick={() => { setNewOnly(v => !v); setClientSearch(''); }}
                    data-testid="new-this-month-card"
                    title="Click to see the new clients this month"
                  >
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500">New This Month {newOnly ? '(showing)' : '›'}</p>
                      <p className="text-3xl font-bold text-green-600">{clientsReport.new_clients}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500">Collected This Month</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        ₹{clientsReport.clients.reduce((s, c) => s + (c.collected_this_month || 0), 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500">Lifetime Collected</p>
                      <p className="text-2xl font-bold text-emerald-700">
                        ₹{clientsReport.clients.reduce((s, c) => s + (c.lifetime_collected || 0), 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search clients..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="pl-9 max-w-sm"
                    data-testid="clients-search"
                  />
                </div>

                {newOnly && (
                  <div className="flex items-center justify-between gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex-wrap">
                    <span className="text-sm text-green-800 font-medium">
                      Showing {clientsReport.clients.filter(c => c.is_new).length} new clients this month
                    </span>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="h-8 text-green-700 border-green-300 hover:bg-green-100" onClick={handleCopyNewClients} data-testid="copy-new-clients">
                        Copy list
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => setNewOnly(false)}>Clear</Button>
                    </div>
                  </div>
                )}

                <Card>
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr className="text-left text-gray-600">
                          <th className="p-3">Client</th>
                          <th className="p-3">Coordinator(s)</th>
                          <th className="p-3 text-right">Lifetime Collected</th>
                          <th className="p-3 text-right">This Month</th>
                          <th className="p-3 text-center">Job Cards</th>
                          <th className="p-3">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientsReport.clients
                          .filter(c => !newOnly || c.is_new)
                          .filter(c => !clientSearch.trim() ||
                            (c.name || '').toLowerCase().includes(clientSearch.trim().toLowerCase()) ||
                            (c.company || '').toLowerCase().includes(clientSearch.trim().toLowerCase()))
                          .sort((a, b) => newOnly ? (new Date(b.created_at || 0) - new Date(a.created_at || 0)) : 0)
                          .map(c => (
                            <tr key={c.client_id} className="border-b last:border-0 hover:bg-gray-50">
                              <td className="p-3">
                                <p className="font-medium flex items-center gap-2">
                                  {c.name}
                                  {c.is_new && <Badge className="bg-green-100 text-green-700 text-[10px]">New</Badge>}
                                </p>
                                {c.company && <p className="text-xs text-gray-500">{c.company}</p>}
                              </td>
                              <td className="p-3 text-blue-600 text-xs">
                                {c.coordinators.length > 0 ? c.coordinators.join(', ') : '-'}
                              </td>
                              <td className="p-3 text-right">
                                <p className="font-medium text-emerald-700">₹{(c.lifetime_collected || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                                <p className="text-[11px] text-gray-400">billed ₹{(c.lifetime_billed || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                              </td>
                              <td className="p-3 text-right font-medium text-emerald-600">
                                {c.collected_this_month > 0 ? `₹${c.collected_this_month.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                              </td>
                              <td className="p-3 text-center">
                                <span className="font-medium">{c.job_cards_open}</span>
                                <span className="text-gray-400"> open / {c.job_cards_total}</span>
                              </td>
                              <td className="p-3 text-xs text-gray-500 whitespace-nowrap">
                                {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
                <p className="text-xs text-gray-400">
                  Collected = payments received (after internal discounts). This-month figures attribute each estimate's payments to its latest payment date.
                </p>
              </>
            ) : (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">Failed to load client KPIs. Switch tabs and back to retry.</CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="projects" className="space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap" data-testid="coordinator-filter-section">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Filter className="w-4 h-4" />
                <span>Filters:</span>
              </div>
              <div className="relative w-[240px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search job card, client, reference..."
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="owner-project-search"
                />
              </div>
              <Select value={selectedCoordinator} onValueChange={setSelectedCoordinator}>
                <SelectTrigger className="w-[180px]" data-testid="coordinator-filter-select">
                  <SelectValue placeholder="All Coordinators" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Coordinators</SelectItem>
                  {coordinators.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DateFilter
                value={dateFilter}
                onChange={setDateFilter}
                startDate={dateFilterStart}
                endDate={dateFilterEnd}
                onDateRangeChange={(start, end) => {
                  setDateFilterStart(start);
                  setDateFilterEnd(end);
                }}
              />
              {(selectedCoordinator !== 'all' || dateFilter !== 'all' || projectSearch.trim()) && (
                <Button variant="ghost" size="sm" onClick={() => { setSelectedCoordinator('all'); setDateFilter('all'); setDateFilterStart(null); setDateFilterEnd(null); setProjectSearch(''); }} data-testid="clear-filter-btn">
                  Clear All Filters
                </Button>
              )}
              <span className="text-sm text-gray-500 ml-auto">
                {filteredProjects.length} job card{filteredProjects.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="grid gap-4">
              {filteredProjects.map(project => {
                const jc = jobCards.find(j => j.project_id === project.id);
                return (
                <Card key={project.id} data-testid={`owner-project-card-${project.project_number}`} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
                    const linkedJC = jobCards.find(j => j.project_id === project.id);
                    if (linkedJC) setViewJobCard(linkedJC);
                  }}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-blue-700">{project.job_card_number || project.project_number}</h3>
                          <Badge className={getStatusColor(jc?.status || project.status)}>
                            {(jc?.status || project.status)?.replace(/_/g, ' ')}
                          </Badge>
                          {jc && isDelayed(jc) && (
                            <Badge className="bg-red-100 text-red-700 border border-red-300 animate-pulse">Delayed</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{project.client_name}</p>
                        {project.reference && (
                          <p className="text-xs text-gray-500 mt-0.5">Ref: {project.reference}</p>
                        )}
                        {project.description && (
                          <p className="text-xs text-gray-500 mt-1">{project.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2">
                          {project.created_by_name && (
                            <span className="text-xs text-blue-600 flex items-center gap-1" data-testid={`coordinator-name-${project.job_card_number || project.project_number}`}>
                              <User className="w-3 h-3" />
                              {project.created_by_name}
                            </span>
                          )}
                          {jc && (
                            <span className="text-xs text-gray-500">
                              Job Card: <strong>{jc.job_card_number}</strong>
                            </span>
                          )}
                          {jc && <PressingBalance jobCard={jc} variant="inline" />}
                          {jc?.cutting_status && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              jc.cutting_status === 'completed' ? 'bg-green-100 text-green-700'
                              : jc.cutting_status === 'started' ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-600'}`}>
                              Cutting: {jc.cutting_status.replace(/_/g, ' ')}
                            </span>
                          )}
                          {(jc?.created_at || project.created_at) && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Created: {new Date(jc?.created_at || project.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                        </div>
                        {/* Estimate/Invoice Info */}
                        {(() => {
                          const est = estimates.find(e => e.job_card_id === jc?.id || e.project_id === project.id);
                          if (!est) return null;
                          return (
                            <div 
                              className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md flex items-center gap-3 cursor-pointer hover:bg-green-100 transition-colors"
                              onClick={() => { setSelectedEstimate(est); setShowEstimateDetail(true); }}
                              data-testid={`estimate-card-${est.estimate_number}`}
                            >
                              <IndianRupee className="w-4 h-4 text-green-600" />
                              <div className="flex items-center gap-3 text-xs">
                                <span className="font-medium text-green-700">{est.estimate_number}</span>
                                <span className="text-green-600 font-semibold">₹ {(est.total_amount || 0).toLocaleString()}</span>
                                <Badge className={est.status === 'sent' ? 'bg-blue-100 text-blue-700' : est.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                                  {est.status || 'draft'}
                                </Badge>
                                <span className="text-gray-500">{est.estimate_lines?.length || 0} items</span>
                                <Eye className="w-3 h-3 text-gray-400" />
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blue-600">{getCompletionPct(project)}%</p>
                        <p className="text-xs text-gray-500">Complete</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
              {filteredProjects.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center">
                    <FolderKanban className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">
                      {selectedCoordinator !== 'all' ? 'No projects for this coordinator' : 'No projects found'}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <PerformanceReports userRole={user?.role} />
          </TabsContent>

        </Tabs>
      </div>

      {/* Job Card Detail Dialog */}
      <Dialog open={!!viewJobCard} onOpenChange={() => setViewJobCard(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="owner-jc-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {viewJobCard?.job_card_number} — {viewJobCard?.customer_name}
            </DialogTitle>
            <DialogDescription>
              Status: {viewJobCard?.status?.replace(/_/g, ' ')} | Coordinator: {viewJobCard?.created_by_name || '-'}
            </DialogDescription>
          </DialogHeader>
          
          {viewJobCard && (
            <div className="space-y-5 py-2">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-gray-500">Customer</p><p className="font-medium">{viewJobCard.customer_name}</p></div>
                <div><p className="text-xs text-gray-500">Coordinator</p><p className="font-medium text-blue-600">{viewJobCard.created_by_name || '-'}</p></div>
                <div><p className="text-xs text-gray-500">Work In Charge</p><p className="font-medium">{viewJobCard.work_in_charge || '-'}</p></div>
                <div><p className="text-xs text-gray-500">Reference</p><p className="font-medium">{viewJobCard.reference || '-'}</p></div>
                <div><p className="text-xs text-gray-500">Mobile</p><p className="font-medium">{viewJobCard.mobile_no || '-'}</p></div>
                <div><p className="text-xs text-gray-500">Date</p><p className="font-medium">{viewJobCard.created_at ? new Date(viewJobCard.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</p></div>
                <div><p className="text-xs text-gray-500">Delivery Date</p><p className="font-medium">{viewJobCard.delivery_date ? new Date(viewJobCard.delivery_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</p></div>
              </div>
              
              <PressingBalance jobCard={viewJobCard} variant="full" className="pb-3" />

              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-orange-50 rounded-lg text-center">
                  <p className="text-xs text-orange-600">Cutting</p>
                  <p className="text-xl font-bold text-orange-700">{viewJobCard.cutting_qty_total || 0}</p>
                  <p className="text-[10px] mt-1 text-orange-500">
                    {(viewJobCard.cutting_status || 'not_started').replace(/_/g, ' ')}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-xs text-blue-600">Pressing</p>
                  {(() => {
                    const p = pressingProgress(viewJobCard);
                    return (
                      <div>
                        <p className="text-xl font-bold text-blue-700">{p.done}/{p.planned}</p>
                        <p className="text-[10px] text-blue-500">{p.balance > 0 ? `${p.balance} left` : 'done'}</p>
                      </div>
                    );
                  })()}
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <p className="text-xs text-purple-600">Boring</p>
                  <p className="text-xl font-bold text-purple-700">{viewJobCard.boring_qty_total || 0}</p>
                </div>
              </div>

              {/* Normal Pressing Lines */}
              {viewJobCard.normal_pressing_lines?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-blue-700">Normal Pressing Lines</h4>
                  <table className="w-full text-xs border">
                    <thead className="bg-blue-50">
                      <tr>
                        <th className="border p-1.5 text-center w-8">#</th>
                        <th className="border p-1.5 text-left">Description</th>
                        <th className="border p-1.5 text-center w-14">Ply Qty</th>
                        <th className="border p-1.5 text-center w-10">Side</th>
                        <th className="border p-1.5 text-center w-14">Total</th>
                        <th className="border p-1.5 text-left">Details</th>
                        <th className="border p-1.5 text-center w-20">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewJobCard.normal_pressing_lines.map((line, i) => (
                        <tr key={line.id || i} className={line.status === 'completed' ? 'bg-green-50' : ''}>
                          <td className="border p-1.5 text-center">{line.sl_no || i + 1}</td>
                          <td className="border p-1.5">{line.description}</td>
                          <td className="border p-1.5 text-center">{line.ply_qty}</td>
                          <td className="border p-1.5 text-center">{line.side}</td>
                          <td className="border p-1.5 text-center font-medium">{line.total || line.ply_qty * line.side}</td>
                          <td className="border p-1.5 text-gray-600">{line.details || '-'}</td>
                          <td className="border p-1.5 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${line.status === 'completed' ? 'bg-green-100 text-green-700' : line.status === 'started' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                              {line.status?.replace(/_/g, ' ') || 'not started'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Hot Pressing Lines */}
              {viewJobCard.hot_pressing_lines?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-red-700">Normal Pressing Lines</h4>
                  <table className="w-full text-xs border">
                    <thead className="bg-red-50">
                      <tr>
                        <th className="border p-1.5 text-center w-8">#</th>
                        <th className="border p-1.5 text-left">Description</th>
                        <th className="border p-1.5 text-center w-14">Ply Qty</th>
                        <th className="border p-1.5 text-center w-10">Side</th>
                        <th className="border p-1.5 text-center w-14">Total</th>
                        <th className="border p-1.5 text-left">Details</th>
                        <th className="border p-1.5 text-center w-20">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewJobCard.hot_pressing_lines.map((line, i) => (
                        <tr key={line.id || i} className={line.status === 'completed' ? 'bg-green-50' : ''}>
                          <td className="border p-1.5 text-center">{line.sl_no || i + 1}</td>
                          <td className="border p-1.5">{line.description}</td>
                          <td className="border p-1.5 text-center">{line.ply_qty}</td>
                          <td className="border p-1.5 text-center">{line.side}</td>
                          <td className="border p-1.5 text-center font-medium">{line.total || line.ply_qty * line.side}</td>
                          <td className="border p-1.5 text-gray-600">{line.details || '-'}</td>
                          <td className="border p-1.5 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${line.status === 'completed' ? 'bg-green-100 text-green-700' : line.status === 'started' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                              {line.status?.replace(/_/g, ' ') || 'not started'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Veneer Pressing Lines */}
              {viewJobCard.veneer_pressing_lines?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-amber-700">PVC & Acrylic Pressing Lines</h4>
                  <table className="w-full text-xs border">
                    <thead className="bg-amber-50">
                      <tr>
                        <th className="border p-1.5 text-center w-8">#</th>
                        <th className="border p-1.5 text-left">Description</th>
                        <th className="border p-1.5 text-center w-14">Ply Qty</th>
                        <th className="border p-1.5 text-center w-10">Side</th>
                        <th className="border p-1.5 text-center w-14">Total</th>
                        <th className="border p-1.5 text-left">Details</th>
                        <th className="border p-1.5 text-center w-20">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewJobCard.veneer_pressing_lines.map((line, i) => (
                        <tr key={line.id || i} className={line.status === 'completed' ? 'bg-green-50' : ''}>
                          <td className="border p-1.5 text-center">{line.sl_no || i + 1}</td>
                          <td className="border p-1.5">{line.description}</td>
                          <td className="border p-1.5 text-center">{line.ply_qty}</td>
                          <td className="border p-1.5 text-center">{line.side}</td>
                          <td className="border p-1.5 text-center font-medium">{line.total || line.ply_qty * line.side}</td>
                          <td className="border p-1.5 text-gray-600">{line.details || '-'}</td>
                          <td className="border p-1.5 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${line.status === 'completed' ? 'bg-green-100 text-green-700' : line.status === 'started' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                              {line.status?.replace(/_/g, ' ') || 'not started'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pressing Diagrams/Images */}
              {viewJobCard.pressing_images?.length > 0 && (
                <div data-testid="owner-pressing-images">
                  <h4 className="text-sm font-semibold mb-2 text-orange-700 flex items-center gap-1">
                    <Image className="w-4 h-4" /> Pressing Diagrams ({viewJobCard.pressing_images.length})
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {viewJobCard.pressing_images.map((url, idx) => (
                      <img 
                        key={idx}
                        src={`${API_BASE}${url}`} 
                        alt={`Pressing diagram ${idx + 1}`}
                        className="w-full h-28 object-cover rounded border cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setViewingPressingImage(url)}
                        data-testid={`owner-pressing-image-${idx}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Finishing Lines */}
              {viewJobCard.finishing_lines?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-pink-700">Finishing Works</h4>
                  <table className="w-full text-xs border">
                    <thead className="bg-pink-50">
                      <tr>
                        <th className="border p-1.5 text-center w-8">#</th>
                        <th className="border p-1.5 text-left">Description</th>
                        <th className="border p-1.5 text-center w-12">Unit</th>
                        <th className="border p-1.5 text-center w-12">Qty</th>
                        <th className="border p-1.5 text-right w-16">Rate</th>
                        <th className="border p-1.5 text-right w-20">Total</th>
                        <th className="border p-1.5 text-left">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewJobCard.finishing_lines.map((line, i) => (
                        <tr key={`line-${i}`}>
                          <td className="border p-1.5 text-center">{i + 1}</td>
                          <td className="border p-1.5">{line.description}</td>
                          <td className="border p-1.5 text-center">{line.unit}</td>
                          <td className="border p-1.5 text-center">{line.qty}</td>
                          <td className="border p-1.5 text-right">{line.rate?.toFixed(2)}</td>
                          <td className="border p-1.5 text-right font-medium">{line.total?.toFixed(2)}</td>
                          <td className="border p-1.5 text-gray-600">{line.details || '-'}</td>
                        </tr>
                      ))}
                      <tr className="font-semibold bg-pink-50">
                        <td className="border p-1.5" colSpan={5} align="right">Total:</td>
                        <td className="border p-1.5 text-right">
                          {viewJobCard.finishing_lines.reduce((sum, l) => sum + (l.total || 0), 0).toFixed(2)}
                        </td>
                        <td className="border p-1.5"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Coordinator Tasks */}
              {viewJobCard.coordinator_tasks?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Coordinator Tasks</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {viewJobCard.coordinator_tasks.map((task, i) => (
                      <div key={task?.task_type || i} className={`p-2 rounded-lg border text-center ${task.status === 'completed' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                        <p className="text-xs font-medium">{task.task_type?.replace(/_/g, ' ')}</p>
                        {task.status === 'completed' ? (
                          <p className="text-[10px] text-green-700 mt-0.5">by {task.performed_by}</p>
                        ) : (
                          <p className="text-[10px] text-gray-400 mt-0.5">Pending</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No data */}
              {!viewJobCard.normal_pressing_lines?.length && !viewJobCard.hot_pressing_lines?.length && !viewJobCard.veneer_pressing_lines?.length && !viewJobCard.finishing_lines?.length && (
                <div className="text-center py-6 text-gray-400">
                  <FileText className="w-10 h-10 mx-auto mb-2" />
                  <p>No pressing or finishing details added yet</p>
                </div>
              )}
            </div>
          )}

          {viewJobCard && (
            <div className="mt-4 space-y-3">
              {['pending_supervisor_review', 'completed', 'sent_to_accounts', 'closed'].includes(viewJobCard.status) && (
                <div className="rounded-lg border border-gray-200 p-3" data-testid="owner-bill-view">
                  <BillView jobCardId={viewJobCard.id} title="Bill" />
                </div>
              )}
              <WorkLogHistory jobCardId={viewJobCard.id} />
              <OperatorEditHistory jobCardId={viewJobCard.id} />
            </div>
          )}

          <DialogFooter className="flex justify-between">
            {viewJobCard?.status === 'closed' && (
              <Button variant="destructive" size="sm" onClick={() => handleDeleteJobCard(viewJobCard.id)} data-testid="delete-closed-jc">
                <Trash2 className="w-4 h-4 mr-1" /> Delete Permanently
              </Button>
            )}
            <Button variant="outline" onClick={() => setViewJobCard(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Full Pressing Image Dialog */}
      <Dialog open={!!viewingPressingImage} onOpenChange={() => setViewingPressingImage(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="owner-view-pressing-image-dialog">
          <DialogHeader>
            <DialogTitle>Pressing Diagram</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-2">
            {viewingPressingImage && (
              <img 
                src={`${API_BASE}${viewingPressingImage}`} 
                alt="Pressing diagram" 
                className="max-h-[60vh] w-auto rounded-lg object-contain"
                data-testid="owner-pressing-image-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Estimate Detail Dialog with Edit History */}
      <Dialog open={showEstimateDetail} onOpenChange={setShowEstimateDetail}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="owner-estimate-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              {selectedEstimate?.estimate_number} — Detail & Edit History
            </DialogTitle>
            <DialogDescription>{selectedEstimate?.customer_name}</DialogDescription>
          </DialogHeader>
          
          {selectedEstimate && (
            <div className="space-y-4 py-2">
              {/* Estimate Info */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Job Card</p>
                  <p className="font-medium text-sm">{selectedEstimate.job_card_number || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Amount</p>
                  <p className="font-bold text-green-600">₹{(selectedEstimate.total_amount || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <div className="flex items-center gap-1">
                    {selectedEstimate.payment_received ? (
                      <Badge className="bg-green-500 text-white text-xs">Paid</Badge>
                    ) : selectedEstimate.sent_to_client ? (
                      <Badge className="bg-purple-100 text-purple-800 text-xs">Sent</Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-800 text-xs">Draft</Badge>
                    )}
                    {selectedEstimate.edit_count > 0 && (
                      <Badge className="bg-amber-100 text-amber-800 text-xs">{selectedEstimate.edit_count}x Edited</Badge>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Last Edited By</p>
                  <p className="font-medium text-sm text-amber-700">{selectedEstimate.last_edited_by || 'N/A'}</p>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <h4 className="text-sm font-semibold mb-2">Current Line Items</h4>
                <table className="w-full text-xs border">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border p-1.5 text-center w-8">#</th>
                      <th className="border p-1.5 text-left">Particulars</th>
                      <th className="border p-1.5 text-right w-14">Qty</th>
                      <th className="border p-1.5 text-center w-14">Unit</th>
                      <th className="border p-1.5 text-right w-16">Rate</th>
                      <th className="border p-1.5 text-right w-20">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedEstimate.estimate_lines?.map((line, i) => (
                      <tr key={`line-${i}`}>
                        <td className="border p-1.5 text-center">{line.sl_no || i + 1}</td>
                        <td className="border p-1.5">{line.particular}</td>
                        <td className="border p-1.5 text-right">{line.qty}</td>
                        <td className="border p-1.5 text-center">{line.unit || ''}</td>
                        <td className="border p-1.5 text-right">{line.rate?.toFixed(2)}</td>
                        <td className="border p-1.5 text-right font-medium">{line.total?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Edit History */}
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <History className="w-4 h-4 text-amber-600" />
                  Edit History ({selectedEstimateLogs.length} edits)
                </h4>
                {selectedEstimateLogs.length > 0 ? (
                  <div className="space-y-2">
                    {selectedEstimateLogs.map((log, idx) => (
                      <div key={log.id || idx} className="border rounded-lg p-3 bg-amber-50/50 border-amber-200">
                        <p className="text-xs font-medium text-amber-700 mb-1.5">
                          {log.edited_by_name} — {new Date(log.edited_at).toLocaleString('en-IN')}
                        </p>
                        <div className="space-y-1">
                          {log.changes?.map((change, ci) => (
                            <div key={ci} className="text-xs flex items-start gap-2 bg-white rounded px-2 py-1">
                              <span className="font-medium text-gray-700 flex-shrink-0">{change.field}:</span>
                              <div className="flex flex-wrap gap-1">
                                {change.old_value && <span className="line-through text-red-500">{change.old_value}</span>}
                                {change.old_value && change.new_value && <span className="text-gray-400">→</span>}
                                {change.new_value && <span className="text-green-600 font-medium">{change.new_value}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-400 text-xs">
                    No edits recorded for this estimate
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEstimateDetail(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receivables detail */}
      <Dialog open={showReceivables} onOpenChange={setShowReceivables}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="receivables-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <IndianRupee className="w-5 h-5" />
              Outstanding — ₹{(receivables?.outstanding || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </DialogTitle>
            <DialogDescription>
              Unpaid and part-paid bills, largest balance first. Age counts from the day the bill was sent.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-gray-600">
                  <th className="p-2">Bill</th>
                  <th className="p-2">Client</th>
                  <th className="p-2 text-right">Balance</th>
                  <th className="p-2 text-center">Age</th>
                  <th className="p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(receivables?.bills || []).map(b => (
                  <tr key={b.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="p-2 font-medium text-blue-700">{b.estimate_number}</td>
                    <td className="p-2">
                      {b.customer_name}
                      {b.job_card_number && <span className="text-xs text-gray-400"> · JC {b.job_card_number}</span>}
                    </td>
                    <td className="p-2 text-right font-semibold text-emerald-700">
                      ₹{(b.balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="p-2 text-center text-gray-500">{b.age_days}d</td>
                    <td className="p-2">
                      {!b.sent_to_client
                        ? <Badge className="bg-red-100 text-red-700">Never sent</Badge>
                        : b.amount_paid > 0
                          ? <Badge className="bg-yellow-100 text-yellow-800">Part paid</Badge>
                          : <Badge className="bg-gray-100 text-gray-700">Sent</Badge>}
                    </td>
                  </tr>
                ))}
                {(receivables?.bills || []).length === 0 && (
                  <tr><td colSpan="5" className="p-6 text-center text-gray-500">Nothing outstanding.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceivables(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Dialog - Shows filtered job cards based on stat card clicked */}
      <Dialog open={showStatsDialog} onOpenChange={setShowStatsDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="stats-dialog">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${getStatsDialogColor()}`}>
              {statsDialogType === 'in_production' && <FolderKanban className="w-5 h-5" />}
              {statsDialogType === 'finished' && <CheckCircle className="w-5 h-5" />}
              {statsDialogType === 'awaiting_start' && <FileText className="w-5 h-5" />}
              {statsDialogType === 'closed' && <Receipt className="w-5 h-5" />}
              {statsDialogType === 'overdue' && <AlertCircle className="w-5 h-5" />}
              {getStatsDialogTitle()}
              {bucketCards && <Badge variant="secondary" className="ml-2">{bucketCards.length}</Badge>}
            </DialogTitle>
            <DialogDescription>
              {BUCKET_META[statsDialogType]?.desc}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 mt-4">
            {bucketCards === null ? (
              <div className="text-center py-8 text-gray-400">
                <p>Loading…</p>
              </div>
            ) : bucketCards.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No job cards in this category</p>
              </div>
            ) : (
              getStatsJobCards().map(jc => {
                const project = projects.find(p => p.id === jc.project_id);
                return (
                  <div
                    key={jc.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-blue-50 hover:shadow-sm transition-all border"
                    onClick={() => handleStatsJobCardClick(jc)}
                    data-testid={`stats-jc-${jc.job_card_number}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="font-semibold text-blue-700 underline decoration-dotted text-lg">
                          #{jc.job_card_number}
                        </p>
                        <Badge className={getStatusColor(jc.status)}>
                          {jc.status?.replace(/_/g, ' ')}
                        </Badge>
                        {isDelayed(jc) && (
                          <Badge className="bg-red-100 text-red-700 border border-red-300 animate-pulse">
                            Delayed
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{project?.client_name || 'Unknown Client'}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        {jc.created_by_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {jc.created_by_name}
                          </span>
                        )}
                        {jc.created_at && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Created: {new Date(jc.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div className="text-center">
                          <p className="font-bold text-blue-600">{jc.cutting_qty_total || 0}</p>
                          <p className="text-gray-500">Cutting</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-amber-600">{jc.pressing_qty_total || 0}</p>
                          <p className="text-gray-500">Pressing</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-purple-600">{jc.boring_qty_total || 0}</p>
                          <p className="text-gray-500">Boring</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowStatsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OwnerDashboard;
